/* eslint-disable camelcase */

import { createSelector } from 'reselect'
import { mergeWith, values, get, invoke, keys } from 'lodash-es'

import {
  RECEIVE_COMMENTS,
  RECEIVE_LIKED_COMMENT,
  COMMENT_ADDED,
  COMMENT_UPDATED,
  RECEIVE_TOP_COMMENTS
} from './comments'
import { CARD_LIKED, CARD_SAVED } from './cardActions'
import {
  RECEIVE_POST,
  RECEIVE_POST_ERROR,
  REPLACE_CHILDREN,
  REMOVE_CHILDREN,
  REQUEST_CHILDREN,
  RECEIVE_CHILDREN
} from './card'
import { RECEIVE_PROFILE, SET_OTHERS_INFO, USER_DOES_NOT_EXIST, USER_UNFOLLOWED } from './profile'
import { RECEIVE_CARD_WITH_UPDATED_TAGS } from './tags'
import { SET_REFERRER_ID } from './referrer'

import {
  RECEIVE_PROFILE_HOME_CONTENT,
  RECEIVE_PINNED_PICKS,
  RECEIVE_PINNED_COLLECTIONS,
  RECEIVE_ALL_PINS,
  RECEIVE_COLLECTIONS,
  RECEIVE_CUSTOM_FEEDS,
  RECEIVE_PICKED_FEEDS,
  RECEIVE_SAVED_FEEDS,
  RECEIVE_ADD_TO_COLLECTION,
  PIN_PICK_SUCCESS,
  UNPIN_PICK_SUCCESS,
  UPDATE_PINS_ORDER,
  RESET_CONTENT,
  RECEIVE_FOLLOWERS,
  RECEIVE_SAVED,
  RECEIVE_USERS,
  CLEAR_USER_COLLECTIONS,
  CARD_UNLINKED,
  REQUEST_ADD_TO_COLLECTION,
  PINS_ORDER_SAVED
} from './userContent'

import { UPDATE_USER_DATA } from './user'
import { CARD_PICKED, RECEIVE_ACCOUNT_PICKS, PICK_REMOVED, CREATE_TEMPORARY_CARD } from './pick'

import {
  CHANGE_LINK_ORDER,
  RECEIVE_CREATED_COLLECTION,
  UPDATE_LINK_ORDER,
  UPDATE_LINK_SORTING,
  LINK_REMOVED,
  COLLECTION_REMOVED,
  RECEIVE_COLLECTION_THUMBNAILS,
  CLEAR_COLLECTION_THUMBNAILS,
  CLEAR_COLLECTION_CHILDREN_LINKS
} from './collection'
import {
  RECEIVE_FEED_SECTIONS,
  RECEIVE_RETRIEVE_IMAGE_FIRST_ITEM,
  RECEIVE_SET_FEED_THUMBNAILS,
  RECEIVE_SET_FEED_CHILDREN
} from './feeds'
import { RECEIVE_ACTIVITIES } from './activities'
import { RECEIVE_USER_DATA } from './cardUserData'

import { fromJS, List } from 'immutable'

import { defaultMergeWithCustomizer, defaultListMerger, reverseListMerger, listReplacer } from '../util/lodashHelpers'

/** *************** Selectors ****************************/
// a "selector creator" that uses lodash's isEqual instead of. uses reselect's defaultMemoize.
// can be handy in the future
// const createDeepEqualSelector = createSelectorCreator(defaultMemoize, isEqual)

const getUserEntities = state => state.entities.getIn(['users', 'entities'])

export const getUsers = createSelector(
  [getUserEntities],
  users => users
)

export const getUsersAsArray = createSelector(
  [getUsers],
  users => values(users)
)

const getCurrentCommentsWithUsers = (state, props) => {
  const { comments, cards, users } = state.entities
  const currentCard = cards.entities[props.id]

  return currentCard
    ? currentCard.comments.map(c => ({ ...comments.entities[c], user: users.entities[comments.entities[c].user] }))
    : []
}

const getUsersById = (state, ids) => state.entities.getIn(['users', 'entities']).filter(i => ids.includes(i.get('id')))

export const makeGetUsersByIds = () => {
  return createSelector(
    [getUsersById],
    users => users
  )
}

export const makeGetCurrentCommentsWithUsers = () => {
  return createSelector(
    [getCurrentCommentsWithUsers],
    currentComments => currentComments
  )
}

export const getCommentById = (state, props) => {
  return state.entities.getIn(['comments', 'entities', props.commentId])
}

export const makeGetCommentById = () => {
  return createSelector(
    [getCommentById],
    comment => comment
  )
}

const getUserById = (state, id) => {
  return state.entities.getIn(['users', 'entities', id])
}

export const makeGetUserById = () => {
  return createSelector(
    [getUserById],
    user => user
  )
}

const getAllUserIds = state => {
  return state.entities.getIn(['users', 'entities']).map(i => i.get('id'))
}

export const makeGetAllUserIds = () => {
  return createSelector(
    [getAllUserIds],
    ids => ids
  )
}

const getFeedById = (state, id) => {
  return state.entities.getIn(['feeds', 'entities', id])
}

export const makeGetFeedById = () => {
  return createSelector(
    [getFeedById],
    feed => feed
  )
}

const getProfileUserByUsername = (state, username) => {
  const { entities } = state
  const users = entities.get('users')
  const feeds = entities.get('feeds')
  const actualUsername = username || get(state.user, 'data.user.username', '')
  const stateUser =
    users
      .get('entities')
      .filter(u => u.get('username') === actualUsername.replace('@', ''))
      .toList()
      .get(0) || fromJS({})
  const userDataStructure = fromJS({
    homeTabContent: [],
    picks: [],
    collections: { public: { arr: [], end: false }, private: { arr: [], end: false } },
    pinnedPicks: { arr: [] },
    pinnedFeeds: { arr: [] },
    savedFeeds: { arr: [] },
    pinnedCollections: { arr: [] },
    savedCards: [],
    feeds: []
  })
  const result = userDataStructure.mergeDeep(stateUser)
  return result
    .set('feeds', result.get('feeds').sort(f => feeds.getIn([f, 'position'])))
    .set('pinnedCollections', result.getIn(['pinnedCollections', 'arr']).filter(i => i))
    .set('pinnedPicks', result.getIn(['pinnedPicks', 'arr']))
    .set(
      'pinnedItems',
      result.getIn(['pinnedPicks', 'arr']).concat(result.getIn(['pinnedCollections', 'arr']).filter(i => i))
    )
    .set('collections', result.getIn(['collections', 'public', 'arr']))
}

export const makeGetProfileUserByUsername = () => {
  return createSelector(
    [getProfileUserByUsername],
    user => user
  )
}

export const getUserIdByUsername = (state, username) => {
  const { entities } = state
  const users = entities.getIn(['users', 'entities'])
  const actualUsername = username || get(state.user, 'data.user.username', '')
  const userFromOldModule = {
    ...get(state, 'user.data.user', {}),
    ...(get(state, 'user.data.auth_token') && { auth_token: get(state, 'user.data.auth_token') })
  }
  const userFromEntities =
    users
      .filter(u => u.get('username') === actualUsername.replace('@', ''))
      .toList()
      .get(0) || fromJS({})
  return username ? userFromEntities : fromJS(userFromOldModule).mergeDeep(userFromEntities)
}

export const makeGetFullUserByUsername = () => {
  return createSelector(
    [getUserIdByUsername],
    user => user
  )
}

const getReferrer = state => {
  const { search } = state.location
  const referrerArr =
    search &&
    search.length > 0 &&
    search
      .replace('?', '')
      .split('&')
      .map(i => i.split('='))
      .find(q => q[0] === 'ref')
  const referrerUsername = referrerArr && referrerArr[1]

  return referrerUsername ? getUserIdByUsername(state, decodeURI(referrerUsername)) : null
}

export const makeGetReferrer = () => {
  return createSelector(
    [getReferrer],
    referrer => referrer
  )
}

const getProfileError = (state, username) => {
  return state.entities.getIn(['users', 'errors', username])
}

export const makeGetProfileError = () => {
  return createSelector(
    [getProfileError],
    error => error
  )
}

const getCurrentFeedIds = (state, props) => {
  const { params } = props.match
  const { feed } = state
  let feedType = get(params, 'feedType', 'newPicks')
  if (feedType === 'popular-picks') {
    feedType = 'popularPicks'
  }
  return feed[feedType] || { error: 'feed_not_found' }
}

export const makeGetCurrentFeedIds = () => {
  return createSelector(
    [getCurrentFeedIds],
    currentFeedIds => invoke(currentFeedIds, 'toArray') || undefined
  )
}

export const getCard = (state, id) => {
  const { entities } = state
  const cards = entities.getIn(['cards', 'entities'])
  const comments = entities.getIn(['comments', 'entities'])
  const users = entities.getIn(['users', 'entities'])
  const card = cards.get(id)
  if (card) {
    return card
      .set('pickAuthor', users.getIn(['card', 'pickAuthor']))
      .set('comments', card.get('comments', List()).map(i => comments.get(i)))
      .set('childrenLinks', card.get('children_links'))
  } else {
    return undefined
  }
}

export const makeGetCard = () => {
  return createSelector(
    [getCard],
    card => card
  )
}

const getCardError = (state, id) => {
  const errors = state.entities.cards.errors
  return errors[id]
}

export const makeGetCardError = () =>
  createSelector(
    [getCardError],
    error => error
  )

/** ***************************************************/

/** *************** Actions **************************/

/** ***************************************************/

/** *************** Selects **************************/

export const selectCards = getState => getState.entities.getIn(['cards', 'entities'])
export const selectFeeds = getState => getState.entities.getIn(['feeds', 'entities'])
export const selectFeedSections = getState => getState.entities.getIn(['feedSections', 'entities'])

/** ***************************************************/

/** *************** State, Reducers *******************/

const me = get(JSON.parse(localStorage.getItem('userData')), 'user', null)

const initialState = fromJS({
  cards: {
    entities: {},
    errors: {},
    fetchStatus: {}
  },
  comments: {
    entities: {},
    errors: {},
    fetchStatus: {}
  },
  users: {
    entities: me ? { [me.id]: me } : {},
    errors: {},
    fetchStatus: {}
  },
  feeds: {
    entities: {},
    errors: {},
    fetchStatus: {}
  },
  feedSections: {
    entities: {},
    errors: {},
    fetchStatus: {}
  },
  tags: {
    entities: {},
    errors: {},
    fetchStatus: {}
  },
  activities: {
    entities: {},
    errors: {},
    fetchStatus: {}
  }
})

export default function(state = initialState, { type, payload }) {
  switch (type) {
    case RECEIVE_PINNED_PICKS:
    case RECEIVE_PINNED_COLLECTIONS:
    case RECEIVE_ALL_PINS:
    case RECEIVE_COLLECTIONS:
    case RECEIVE_CUSTOM_FEEDS:
    case RECEIVE_POST:
    case RECEIVE_PROFILE:
    case RECEIVE_PROFILE_HOME_CONTENT:
    case SET_OTHERS_INFO:
    case UPDATE_USER_DATA:
    case RECEIVE_ACCOUNT_PICKS:
    case CARD_PICKED:
    case RECEIVE_FEED_SECTIONS:
    case RECEIVE_TOP_COMMENTS:
    case RECEIVE_ACTIVITIES:
    case RECEIVE_SAVED:
    case RECEIVE_USERS:
    case RECEIVE_USER_DATA:
    case COMMENT_ADDED:
    case RECEIVE_CREATED_COLLECTION: {
      let listMerger = defaultListMerger
      const { entities } = payload.data
      const {
        cards = {},
        users = {},
        comments = {},
        tags = {},
        feeds = {},
        feedSections = {},
        activities = {}
      } = entities
      if (type === COMMENT_ADDED) {
        const card = cards[keys(entities.cards)[0]]
        const commentId = comments[keys(entities.comments)[0]].id
        card.latest_comment = commentId
        if (values(entities.users)[0].id === payload.referrerId) {
          card.referrer_comment = commentId
        }
        card.my_comment = commentId
      }
      if (type === CARD_PICKED || type === RECEIVE_CREATED_COLLECTION) {
        listMerger = reverseListMerger
      }
      const mergeObject = fromJS({
        cards: { entities: cards, fetchStatus: !!cards },
        users: { entities: users, fetchStatus: !!users },
        comments: { entities: comments, fetchStatus: !!comments },
        feeds: { entities: feeds, fetchStatus: !!feeds },
        feedSections: { entities: feedSections, fetchStatus: !!feedSections },
        tags: { entities: tags, fetchStatus: !!tags },
        activities: { entities: activities, fetchStatus: !!activities }
      })
      return state
        .setIn(
          ['users', 'entities'],
          state.getIn(['users', 'entities']).mergeWith(listMerger, mergeObject.getIn(['users', 'entities']))
        )
        .setIn(
          ['cards', 'entities'],
          state.getIn(['cards', 'entities']).mergeWith(listMerger, mergeObject.getIn(['cards', 'entities']))
        )
        .setIn(
          ['comments', 'entities'],
          state.getIn(['comments', 'entities']).mergeWith(listMerger, mergeObject.getIn(['comments', 'entities']))
        )
        .setIn(
          ['feeds', 'entities'],
          state.getIn(['feeds', 'entities']).mergeWith(listMerger, mergeObject.getIn(['feeds', 'entities']))
        )
        .setIn(
          ['feedSections', 'entities'],
          state
            .getIn(['feedSections', 'entities'])
            .mergeWith(listMerger, mergeObject.getIn(['feedSections', 'entities']))
        )
        .setIn(
          ['tags', 'entities'],
          state.getIn(['tags', 'entities']).mergeWith(listMerger, mergeObject.getIn(['tags', 'entities']))
        )
        .setIn(
          ['activities', 'entities'],
          state.getIn(['activities', 'entities']).mergeWith(listMerger, mergeObject.getIn(['activities', 'entities']))
        )
    }

    case RECEIVE_RETRIEVE_IMAGE_FIRST_ITEM: {
      return state.setIn(['feeds', 'entities', payload.id, 'thumbnail'], payload.thumbnail)
    }

    case RECEIVE_SET_FEED_THUMBNAILS: {
      return state.setIn(['feeds', 'entities', payload.id, 'feedThumbnails'], payload.thumbnails)
    }

    case RECEIVE_SET_FEED_CHILDREN: {
      return state.setIn(['feeds', 'entities', payload.id, 'feed_children'], payload.children)
    }

    case RECEIVE_CARD_WITH_UPDATED_TAGS: {
      const { entities } = payload.data
      const { cards } = entities
      const mergeObject = fromJS({
        cards: { entities: cards }
      })
      return state.setIn(
        ['cards', 'entities'],
        state.getIn(['cards', 'entities']).mergeWith(listReplacer, mergeObject.getIn(['cards', 'entities']))
      )
    }
    case RECEIVE_SAVED_FEEDS:
    case RECEIVE_PICKED_FEEDS: {
      const user = Object.keys(payload.data.entities.users)[0]
      const data = Object.keys(payload.data.entities.feeds || [])
      return state.setIn(['users', 'entities', user, 'feeds'], new List(data))
    }

    case PIN_PICK_SUCCESS: {
      const { cardId, feed, userId } = payload
      const array = ['users', 'entities', userId, feed === 'picks' ? 'pinnedPicks' : 'pinnedCollections', 'arr']
      return state
        .setIn(array, state.getIn(array, []).push(cardId))
        .setIn(['cards', 'entities', cardId, 'is_pinned_by_me'], feed === 'picks')
    }
    case UNPIN_PICK_SUCCESS: {
      const { cardId, feed, userId } = payload
      const array = ['users', 'entities', userId, feed === 'picks' ? 'pinnedPicks' : 'pinnedCollections', 'arr']
      return state
        .setIn(array, state.getIn(array, []).filter(id => id !== cardId))
        .setIn(['cards', 'entities', cardId, 'is_pinned_by_me'], false)
    }
    case RECEIVE_POST_ERROR: {
      const [key, value] = payload.data
      const mergeObject = {
        cards: {
          errors: {
            [key]: value
          }
        }
      }
      const result = mergeWith({}, state, mergeObject, defaultMergeWithCustomizer)
      return result
    }
    case USER_DOES_NOT_EXIST: {
      const [key, value] = payload.data
      const mergeObject = {
        users: {
          errors: {
            [key]: value
          }
        }
      }
      const result = mergeWith({}, state, mergeObject, defaultMergeWithCustomizer)
      return result
    }
    case RECEIVE_COMMENTS: {
      const { entities, result } = payload.data
      const object = fromJS(entities)
      const cards = object.get('cards')
      const comments = object.get('comments')
      const users = object.get('users')
      return state
        .setIn(['cards', 'entities'], state.getIn(['cards', 'entities']).mergeWith(defaultListMerger, cards))
        .setIn(['comments', 'entities'], state.getIn(['comments', 'entities']).mergeWith(defaultListMerger, comments))
        .setIn(['users', 'entities'], state.getIn(['users', 'entities']).mergeWith(defaultListMerger, users))
        .setIn(['cards', 'entities', result, 'comment_count'], cards.getIn([result, 'comments', 'length']))
    }
    case RECEIVE_LIKED_COMMENT: {
      const { id, unlike } = payload
      const array = ['comments', 'entities', id]
      const likes = state.getIn([...array, 'likes']) + (unlike ? -1 : 1)
      return state.mergeIn(array, { liked_by_current_user: !unlike, likes })
    }

    case REQUEST_ADD_TO_COLLECTION: {
      const array = ['cards', 'entities', payload.collectionId]
      return state.setIn(array.concat('loading'), true)
    }

    case RECEIVE_ADD_TO_COLLECTION: {
      const array = ['cards', 'entities', payload.collectionId]
      return state.setIn(array.concat('new_card_is_present'), payload.added).setIn(array.concat('loading'), false)
    }
    case COMMENT_UPDATED: {
      const { id, content } = payload
      return state.setIn(['comments', 'entities', id, 'content'], content)
    }
    case CARD_LIKED: {
      const { id, unlike, userId } = payload
      const likedCard = state.getIn(['cards', 'entities', id])
      const lWL = likedCard.get('latest_who_liked', [])
      return state.mergeIn(['cards', 'entities', id], {
        liked_by_me: !unlike,
        likes: likedCard.get('likes', 0) + (unlike ? -1 : 1),
        latest_who_liked: unlike ? lWL.filter(i => i !== userId) : lWL.push(userId)
      })
    }
    case CARD_SAVED: {
      const { id, unsave, userId } = payload
      const user = state.getIn(['users', 'entities', userId])
      return state
        .setIn(
          ['users', 'entities', userId, 'savedCards'],
          unsave ? user.get('savedCards', []).filter(i => i !== id) : user.get('savedCards', fromJS([])).unshift(id)
        )
        .setIn(['cards', 'entities', id, 'is_saved_by_me'], !unsave)
        .setIn(['cards', 'entities', id, 'id'], id)
    }
    case CHANGE_LINK_ORDER: {
      const { collectionId, cardId, position } = payload
      const array = ['cards', 'entities', collectionId, 'children_links']
      const children = state.getIn(array)
      const filteredCopy = children.filter(l => l !== cardId)
      return state.setIn(
        array,
        filteredCopy
          .slice(0, position)
          .push(cardId)
          .merge(filteredCopy.slice(position))
      )
    }
    case UPDATE_PINS_ORDER: {
      const { oldIndex, newIndex, userId, feed } = payload
      let array
      if (feed === 'picks') {
        array = ['users', 'entities', userId, 'pinnedPicks', 'arr']
      } else if (feed === 'collections') {
        array = ['users', 'entities', userId, 'pinnedCollections', 'arr']
      } else {
        array = ['users', 'entities', userId, 'allPinnedItems']
      }
      const el = state.getIn([...array, oldIndex])
      return state.setIn(
        array,
        state
          .getIn(array)
          .splice(oldIndex, 1)
          .splice(newIndex, 0, el)
      )
    }
    case PINS_ORDER_SAVED: {
      const { userId, cardIds } = payload
      return state.setIn(['users', 'entities', userId, 'allPinnedItems'], cardIds)
    }
    case UPDATE_LINK_ORDER: {
      const { oldIndex, newIndex, collectionId } = payload
      const array = ['cards', 'entities', collectionId, 'children_links']
      const links = state.getIn(array)
      const el = links.splice(oldIndex, 1).get(0)
      return state.setIn(array, links.splice(oldIndex, 1).splice(newIndex, 0, el))
    }
    case UPDATE_LINK_SORTING: {
      const array = ['cards', 'entities', payload.collectionId, 'children_links']
      const links = state.getIn(array)
      const el = links.get(payload.oldIndex)
      return state.setIn(array, links.splice(payload.oldIndex, 1).splice(payload.newIndex, 0, el))
    }

    case REPLACE_CHILDREN: {
      const newArray = payload.children.map(item => item.id)
      return state.setIn(['cards', 'entities', payload.cardId, 'children_links'], new List(newArray))
    }

    case REMOVE_CHILDREN: {
      return state.setIn(['cards', 'entities', payload.cardId, 'children_links'], new List())
    }

    case REQUEST_CHILDREN: {
      return state.setIn(['cards', 'entities', payload.cardId].concat('children_loading'), true)
    }

    case RECEIVE_CHILDREN: {
      return state.setIn(['cards', 'entities', payload.cardId].concat('children_loading'), false)
    }

    case LINK_REMOVED: {
      const { index, collectionId } = payload
      const array = ['cards', 'entities', collectionId, 'children_links']
      return state
        .setIn(array, state.getIn(array).splice(index, 1))
        .setIn(
          ['cards', 'entities', collectionId, 'element_count'],
          state.getIn(['cards', 'entities', collectionId, 'element_count']) - 1
        )
    }
    case PICK_REMOVED:
    case COLLECTION_REMOVED: {
      const { id, userId } = payload
      const userArray = ['users', 'entities', userId]
      const cardArray = ['cards', 'entities', id]
      const user = state.getIn(userArray)
      const card = state.getIn(cardArray)
      if (type === PICK_REMOVED) {
        const { referrer } = payload
        const previousPickPrivate = card && card.get('is_private_pick')
        const previousPickByReferrer = card && card.get('is_picked_by_referrer')
        const referrerId = referrer && referrer.get('id')
        const userIdToCompare = referrerId || userId
        const userTheSameAsSelected = referrerId === userId

        return state.setIn([...userArray, 'picks'], user.get('picks', []).filter(i => i !== id)).mergeIn(cardArray, {
          picks_amount: card.get('picks_amount') - (!previousPickPrivate && userTheSameAsSelected ? 1 : 0),
          is_picked_by_me: false,
          is_private_pick: false,
          is_picked_by_referrer: referrer && referrer.get('id') === user.get('id') ? false : previousPickByReferrer,
          pickers: card.get('pickers').filter(i => i !== userIdToCompare)
        })
      } else {
        if (user.getIn(['collections', 'public', 'arr'])) {
          return state.setIn(
            [...userArray, 'collections', 'public', 'arr'],
            user.getIn(['collections', 'public', 'arr']).filter(i => i !== id)
          )
        }
      }
      return state
    }
    case RESET_CONTENT: {
      return state.mergeIn(['users', 'entities', payload], { pinnedPicks: { arr: [] }, pinnedCollections: { arr: [] } })
    }
    case RECEIVE_FOLLOWERS: {
      const key = payload.isFollowings ? 'followings' : 'followers'
      const array = ['users', 'entities', payload.id, key]
      const value = state.getIn(array)
      return state.setIn(array, value.concat(payload.data.result))
    }
    case SET_REFERRER_ID: {
      const { id, oldRefId } = payload
      const cards = state.getIn(['cards', 'entities'])
      cards.forEach(entity => {
        state = state
          .setIn(['cards', 'entities', entity.get('id'), 'thumbnails', oldRefId], entity.get('collectionThumbnails'))
          .mergeIn(
            ['cards', 'entities', entity.get('id')],
            entity.setIn(['referrerComments', oldRefId], entity.get('referrer_comment'))
          )
          .setIn(
            ['cards', 'entities', entity.get('id'), 'collectionThumbnails'],
            entity.getIn(['thumbnails', id], new List())
          )
          .setIn(
            ['cards', 'entities', entity.get('id'), 'referrer_comment'],
            entity.getIn(['referrerComments', id], null)
          )
      })
      return state
    }
    case RECEIVE_COLLECTION_THUMBNAILS: {
      const { cardId, images } = payload
      const array = ['cards', 'entities', cardId, 'collectionThumbnails']
      const thumbs = state.getIn(array)
      return state.setIn(array, thumbs.concat(images).filter(i => i))
    }
    case CLEAR_COLLECTION_THUMBNAILS: {
      const cards = state
        .getIn(['cards', 'entities'])
        .filter(i => payload.includes('i')) // TODO: wtf?
        .map(i => i.set('collectionThumbnails', new List()))
      return state.mergeIn(['cards', 'entities'], cards)
    }
    case CLEAR_COLLECTION_CHILDREN_LINKS: {
      return state.setIn(['cards', 'entities', payload.collectionId, 'children_links'], new List())
    }
    case CLEAR_USER_COLLECTIONS: {
      return state.setIn(['users', 'entities', payload, 'collections', 'public', 'arr'], [])
    }
    case CREATE_TEMPORARY_CARD: {
      const newState = { ...state }
      const { feed, userId } = payload
      const userFeed = feed === 'cards' ? 'picks' : 'savedCards'
      const user = newState.users.entities[userId]
      if (user[userFeed]) {
        user[userFeed].unshift('uploading')
      }
      return newState
    }
    case USER_UNFOLLOWED: {
      const { userId, profileId } = payload
      const followingsArray = ['users', 'entities', userId, 'followings']
      const followersCountArray = ['users', 'entities', profileId, 'followersAmount']
      return state
        .setIn(followingsArray, state.getIn(followingsArray, []).filter(i => i !== profileId))
        .setIn(['users', 'entities', profileId, 'followByCurrentUser'], false)
        .setIn(followersCountArray, state.getIn(followersCountArray) - 1)
    }
    case CARD_UNLINKED: {
      const { collectionId, cardId } = payload
      const links = ['cards', 'entities', collectionId, 'children_links']
      return state.setIn(links, state.getIn(links).filter(i => i !== cardId))
    }
    default: {
      return state
    }
  }
}
